<template lang="html">
  <item-editor-dialog
    :id="selectedItemId"
    ref="editForm"
    v-model="selectedPanel"
    :show="showItem"
    :clone-id="cloneId"
    :title="pageTitle"
    :on-close="doClose"
    :context-menu="defaultContextMenu"
    :lock-timer="lockTimerSettings"
    :loading="isDataLoading"
    :errors="errors"
    :edit-panel-id="editPanel"
    :audit-panel-id="auditPanel"
    :audit="auditHistorySettings"
    :buttons="pageBarButtons"
  >
    <!-- START OF FORM FIELDS -->
    <v-flex class="pa-3">
      <v-text-field
        v-model.trim="itemData.name"
        :label="type + ' Name'"
        :rules="rules().required('Name')"
        :error-messages="getPathErrors(jsonPath, 'name')"
        :disabled="isFormDisabled"
        required
        @input="clearPathErrors(jsonPath, 'name')"
      />

      <v-autocomplete
        v-model="itemData.locationId"
        label="Location"
        placeholder="Select Location"
        :items="locations"
        :rules="rules().required()"
        :required="true"
        :clearable="false"
        :readonly="!isNewItem"
        auto
        :error-messages="getPathErrors(jsonPath, 'locationId')"
        :disabled="isFormDisabled"
        item-text="name"
        item-value="id"
        @input="clearPathErrors(jsonPath, 'locationId')"
      />

      <v-textarea
        v-model.trim="itemData.notes"
        label="Comments"
        :rows="1"
        auto-grow
        :error-messages="getPathErrors(jsonPath, 'notes')"
        :disabled="isFormDisabled"
        @input="clearPathErrors(jsonPath, 'notes')"
      />

      <v-layout
        wrap
        class="d-flex justify-space-around"
      >
        <v-boolean
          v-model="itemData.allowEvent"
          label="Allow Use With Events"
          :error-messages="getPathErrors(jsonPath, 'allowEvent')"
          :field-errors="getPathErrors(jsonPath, 'allowEvent')"
          :disabled="isFormDisabled"
        />
        <v-boolean
          v-model="itemData.allowMealPeriod"
          label="Allow Use With Meal Periods"
          :error-messages="getPathErrors(jsonPath, 'allowMealPeriod')"
          :field-errors="getPathErrors(jsonPath, 'allowMealPeriod')"
          :disabled="isFormDisabled"
        />
        <v-boolean
          v-if="$ftCS.DINING_ENABLE_FLOOR_PLAN"
          v-model="itemData.useFloorPlan"
          label="Floor Plan"
          :error-messages="getPathErrors(jsonPath, 'itemData.floorPlan')"
          :field-errors="getPathErrors(jsonPath, 'itemData.floorPlan')"
          :disabled="isFormDisabled"
        />
      </v-layout>
      <template v-if="$ftCS.DINING_ENABLE_FLOOR_PLAN">
        <v-expand-transition>
          <floor-plan-configurator
            v-if="itemData.useFloorPlan"
            ref="floorPlanConfigurator"
            v-bind="itemData"
            :tables="itemData.tables.filter(t => !t.disabled && t.showOnFloorPlan)"
            :join-group-options="groupNames"
            :new-table-default="newTableDefault"
            :new-shape-default="{
              tableConfigId: itemData.id,
              height: 100,
              width: 100,
              shape: 'CIRCLE',
              fill: '#000000',
              name: getUniqueName('basic-shape-', 'shapes'),
              textScale: 2,
              textFill: 'white',
              rotation: 0,
              flipped: false,
              showOnFloorPlan: true,
            }"
            :new-label-default="{
              tableConfigId: itemData.id,
              text: 'Label',
              fill: 'black',
              name: getUniqueName('label-', 'labels'),
              rotation: 0,
              fontFamily: 'Arial',
              scaleX: 2,
              scaleY: 2,
              flipped: false,
              showOnFloorPlan: true,
            }"
            :new-icon-default="{
              tableConfigId: itemData.id,
              name: getUniqueName('icon-', 'icons'),
              color: 'black',
              icon: 'star-plus',
              rotation: 0,
              scaleX: 0.35,
              scaleY: 0.35,
              flipped: false,
              showOnFloorPlan: true,
            }"
            :disabled="isFormDisabled"
            @input="(target, index, replacement) => itemData[target].splice(index, 1, replacement)"
            @delete-element="deleteElement"
            @add-element="(target, item) => {
              if (!itemData[target]) $set(itemData, target, [])
              itemData[target].push(item)
            }"
            @update-field="(field, value) => $set(itemData, field, value)"
          />
        </v-expand-transition>
      </template>
    </v-flex>
    <v-row no-gutters>
      <v-spacer />
    </v-row>
    <detail-editor-table
      v-model="itemData.tables"
      type="Table"
      :json-path="buildJsonPath('tables')"
      :get-path-errors="getPathErrors"
      :clear-path-errors="clearPathErrors"
      :move-path-errors="movePathErrors"
      :disabled="isFormDisabled"
      sortable
      :disable-delete="disableTableDelete"
      :delete-item-check="(item, index)=>!!item._localAddition"
      hide-default-footer
      :item-default="{
        id: null,
        name: null,
        quantity: null,
        autoAssignQuantity: 0,
        tableConfigId: itemData.id,
        tableId: null,
        tableName: null,
        minCovers: null,
        maxCovers: null,
        maxJoinCovers: null,
        disabled: false,
        group: null,
        definitionQuantity: null,
        definitionMinCovers: null,
        definitionMaxCovers: null,
        definitionMaxJoinCovers: null,
        _localAddition: true,
        x: 100,
        y: 100,
        height: 100,
        width: 100,
        name: getUniqueName(),
        floatingBoxPosition: 'L_R',
        shape: 'SQUARE_4',
        flipped: false,
        rotation: 0,
        showOnFloorPlan: true,
      }"
      :headers="tablesHeaders"
      @error="addError"
      @add="configDetailAdded"
    >
      <template #title-right>
        <v-card
          v-if="$ftCS.DINING_ENABLE_FLOOR_PLAN"
          class="px-10 elevation-0 transparent font-weight-regular"
          :disabled="isFormDisabled"
        >
          <div
            class="d-flex justify-end"
            style="cursor: pointer;"
            @click="showAllOnFloorPlan((itemData.tables || []).some(t => !t.showOnFloorPlan))"
          >
            <v-simple-checkbox
              :value="!(itemData.tables || []).some(t => !t.showOnFloorPlan)"
              label="Show All on Floor Plan"
              :disabled="isFormDisabled"
              :ripple="false"
              @input="showAllOnFloorPlan((itemData.tables || []).some(t => !t.showOnFloorPlan))"
            />
            <span
              class="v-label align-self-center"
            >
              Show All on Floor Plan
            </span>
          </div>
          <v-expand-transition>
            <div
              v-if="showPartialError"
              class="v-messages error--text"
            >
              Batch too large. Configure current tables and try again.
            </div>
          </v-expand-transition>
        </v-card>
      </template>
      <template
        slot="group"
        slot-scope="props"
      >
        <v-combobox
          placeholder="Group Name"
          :items="groupNames"
          :value="props.item.group"
          :clearable="true"
          auto
          :error-messages="props.errorMessages"
          :disabled="isFormDisabled"
          @input="props.setCellValue(props.item, props.header.value, props.header.editor, props.row, $event)"
          @change="updateGroupNamesDelay()"
        />
      </template>

      <template #showOnFloorPlan="{ item }">
        <v-switch
          v-model="item.showOnFloorPlan"
          @change="v => {
            if (v) {
              initializeTableForFloorPlan(item)
              setTableFocus(item, v)
            }
          }"
        />
      </template>

      <template
        slot="minCovers"
        slot-scope="props"
      >
        <v-text-field
          :value="props.item.minCovers"
          type="number"
          :placeholder="notValidNumber(props.item.definitionMinCovers) ? null : props.item.definitionMinCovers.toString()"
          :error-messages="props.errorMessages"
          :disabled="isFormDisabled"
          @input="props.setCellValue(props.item, props.header.value, props.header.editor, props.row, $event, 'integer')"
        />
      </template>

      <template
        slot="maxCovers"
        slot-scope="props"
      >
        <v-text-field
          :value="props.item.maxCovers"
          type="number"
          :placeholder="notValidNumber(props.item.definitionMaxCovers) ? null : props.item.definitionMaxCovers.toString()"
          :error-messages="props.errorMessages"
          :disabled="isFormDisabled"
          @input="props.setCellValue(props.item, props.header.value, props.header.editor, props.row, $event, 'integer')"
        />
      </template>

      <template
        slot="maxJoinCovers"
        slot-scope="props"
      >
        <v-text-field
          :value="props.item.maxJoinCovers"
          type="number"
          :placeholder="notValidNumber(props.item.definitionMaxJoinCovers) ? null : props.item.definitionMaxJoinCovers.toString()"
          :error-messages="props.errorMessages"
          :disabled="isFormDisabled"
          @input="props.setCellValue(props.item, props.header.value, props.header.editor, props.row, $event, 'integer')"
        />
      </template>

      <template
        slot="quantity"
        slot-scope="props"
      >
        <v-text-field
          :value="props.item.quantity"
          type="number"
          :placeholder="notValidNumber(props.item.definitionQuantity) ? null : props.item.definitionQuantity.toString()"
          :error-messages="props.errorMessages"
          :disabled="isFormDisabled || props.item.showOnFloorPlan"
          @input="props.setCellValue(props.item, props.header.value, props.header.editor, props.row, $event, 'integer')"
        />
      </template>

      <template
        slot="autoAssignQuantity"
        slot-scope="props"
      >
        <v-text-field
          :value="props.item.autoAssignQuantity"
          type="number"
          :error-messages="props.errorMessages"
          :disabled="isFormDisabled"
          @input="props.setCellValue(props.item, props.header.value, props.header.editor, props.row, $event, 'integer')"
        />
      </template>

      <template #disabled="{ item }">
        <v-boolean
          :value="item.disabled"
          invert
          :disabled="isFormDisabled"
          @input="v => {
            item.disabled = v
            if (v) item.showOnFloorPlan = false
          }"
        />
      </template>
    </detail-editor-table>

    <v-flex class="pa-3">
      <v-boolean
        v-model="itemData.disabled"
        label="Enabled"
        invert
        :error-messages="getPathErrors(jsonPath, 'disabled')"
        :field-errors="getPathErrors(jsonPath, 'disabled')"
        :disabled="isFormDisabled"
      />
    </v-flex>
    <!-- END OF FORM FIELDS -->
  </item-editor-dialog>
</template>

<script>
import {itemEdit, withChildren, DetailEditorTable} from '@/ft-vue-core'
import canvasHelpers from './floorPlan/mixins/canvasHelpers'
import FloorPlanConfigurator from './floorPlan/FloorPlanConfigurator'

var itemDefault = {
  id: null,
  name: '',
  notes: '',
  locationId: null,
  allowEvent: true,
  allowMealPeriod: true,
  disabled: false,
  recordLockExpiration: null,
  tables: [],
  gridDensity: 1,
}

export default {
  name: 'TableConfigurationItem',
  components: {
    DetailEditorTable,
    FloorPlanConfigurator,
  },
  mixins: [itemEdit, withChildren, canvasHelpers],
  data: function () {
    return {
      type: 'Table Configuration',
      types: 'Table Configuration',
      apiRecordCommand: 'diningTableConfiguration',
      itemDefault: itemDefault,
      cachedTables: [], // Store previous table settings when switching locations
      locations: [],
      groupNames: [],
      cloner: {
        id: null,
        tables: {
          _cloneMode: Array,
          id: null,
          tableConfigId: null
        }
      },
      showPartialError: false,
    }
  },
  computed: {
    newTableDefault () {
      return {
        id: null,
        quantity: 1,
        autoAssignQuantity: 0,
        tableConfigId: this.itemData.id,
        tableId: null,
        tableName: null,
        minCovers: null,
        maxCovers: null,
        maxJoinCovers: null,
        disabled: false,
        group: null,
        definitionQuantity: null,
        definitionMinCovers: null,
        definitionMaxCovers: null,
        definitionMaxJoinCovers: null,
        _localAddition: true,
        height: 100,
        width: 100,
        scaleX: 1,
        scaleY: 1,
        name: this.getUniqueName(),
        floatingBoxPosition: 'L_R',
        shape: 'SQUARE_4',
        flipped: false,
        rotation: 0,
        showOnFloorPlan: true,
      }
    },
    tablesHeaders () {
      let result = [
        this.headerDragSort(),
        this.headerEditor({label: 'Table', field: 'tableName', editor: {required: true, rules: this.rules().required()}}),
        this.headerEditor({label: 'Join Group', field: 'group', editor: {type: 'slot'}}),
      ]
      if (this.$ftCS.DINING_ENABLE_FLOOR_PLAN) result.push(this.headerEditor({
        label: 'Show on Floor Plan',
        field: 'showOnFloorPlan',
        editor: {type: 'slot'},
        thin: true}
      ))
      result.push(
        this.headerEditor({label: 'Min Covers', field: 'minCovers', editor: {type: 'slot'}, thin: true}),
        this.headerEditor({label: 'Max Covers', field: 'maxCovers', editor: {type: 'slot'}, thin: true}),
        this.headerEditor({label: 'Max Join Covers', field: 'maxJoinCovers', editor: {type: 'slot'}, thin: true}),
        this.headerEditor({label: 'Quantity', field: 'quantity', editor: {type: 'slot'}, thin: true}),
        this.headerEditor({label: 'Auto Assign Quantity', field: 'autoAssignQuantity', editor: {type: 'slot'}, thin: true}),
        this.headerEditor({label: 'Enabled', field: 'disabled', editor: {type: 'slot'}, thin: true})
      )
      return result
    },
    selectedLocation () {
      const locationId = this.itemData.locationId
      return this.locations.find(l => l.id === locationId)
    },
    selectedLocationTables () {
      return (this.selectedLocation || {tables: []}).tables
    },
    disableTableDelete () {
      // Don't show delete column for table editor if there are no local additions (We currently don't allow deleting tables here -- only ones that haven't been saved yet.)
      return !(this.itemData.tables || []).find(t => t._localAddition)
    },
    xAxisMap () {
      return this.itemData.tables?.reduce((acc, r) => {
        acc[r.x] = r
        return acc
      }, {}) ?? {}
    },
    yAxisMap () {
      return this.itemData.tables?.reduce((acc, r) => {
        acc[r.y] = r
        return acc
      }, {}) ?? {}
    }
  },
  watch: {
    selectedLocationTables () {
      this.updateFilteredTables()
    },
    selectedItemId () {
      this.filteredTablesInit = false
    }
  },
  methods: {
    /**
     * Set values of fields on a Table that will initialize it for use with the Floor Plan.
     *
     * @param {Object} table Table that will be initialized to work with Floor Plan
     * @param {...Object} adtl Objects of format {parameter: value} to set additional fields on Table for initalization
     */
    initializeTableForFloorPlan (table, ...adtl) {
      for (let key in this.newTableDefault) {
        switch (key) {
        case 'disabled':
          table.disabled = false
          break
        case 'quantity':
          table.quantity = 1
          break
        }
        if (table[key] == null) table[key] = this.newTableDefault[key]
      }
      Object.assign(table, ...adtl)
      return table
    },
    /**
     * TEMP: We will _always_ auto-clear reservation statuses on Floor Plan to begin
     * TODO: Remove this later if we decide to do more with this feature
     * @param {Function} saveCallback callback function to finish the save process
     */
    beforeSaveItem (saveCallback) {
      this.itemData.autoClearReservationStatus = true
      saveCallback()
    },
    setTableFocus (table, value) {
      if (this.configDetailAdded(table) === false) {
        this.$set(this, 'showPartialError', true)
        return false
      } else this.$set(this, 'showPartialError', false)
      table.name = this.getUniqueName()
      if (value && table.disabled) table.disabled = false
      const {shape, floatingBoxPosition} = table
      if (!shape) table.shape = 'SQUARE_4'
      if (!floatingBoxPosition) table.floatingBoxPosition = 'L_R'
      const floorPlanConfigurator = this.$refs.floorPlanConfigurator
      if (!floorPlanConfigurator) return
      const floorPlanCanvas = floorPlanConfigurator.$refs.floorPlanCanvas
      if (!floorPlanCanvas) return
      if (value) {
        this.$nextTick(() => {
          floorPlanCanvas.setTransformer(value ? {tableName: table.name} : {})
          floorPlanConfigurator.selectedTable = value ? table : undefined
        })
      } else {
        floorPlanCanvas.setTransformer(value ? {tableName: table.name} : {})
        floorPlanConfigurator.selectedTable = value ? table : undefined
      }
    },
    deleteElement (table, listLocation = 'tables') {
      const spliceIndex = this.itemData[listLocation]?.findIndex(t => t.name === table.name)
      if (spliceIndex !== -1) {
        if (listLocation === 'tables' && !table._localAddition) {
          ['disabled', 'showOnFloorPlan'].forEach(p => this.$set(this.itemData[listLocation][spliceIndex], p, p === 'disabled'))
        } else this.itemData[listLocation]?.splice(spliceIndex, 1)
        const floorPlanConfigurator = this.$refs.floorPlanConfigurator
        if (!floorPlanConfigurator) return
        const floorPlanCanvas = floorPlanConfigurator.$refs.floorPlanCanvas
        if (!floorPlanCanvas) return
        floorPlanCanvas.resetTransformer()
      }
    },
    showAllOnFloorPlan (value) {
      let breakOut = false
      for (let i = 0; i < this.itemData.tables?.length ?? 0; i++) {
        if (breakOut) break
        let t = this.itemData.tables[i]
        if (!value) {
          this.$set(t, 'x', null)
          this.$set(t, 'y', null)
          this.$set(t, 'showOnFloorPlan', false)
        } else {
          if (t.showOnFloorPlan) {/* Do Nothing */}
          else if (this.setTableFocus(t) === false) breakOut = true
          if (!breakOut) this.$set(this.itemData.tables, i, this.initializeTableForFloorPlan(t, {showOnFloorPlan: true}))
        }
      }
    },
    getUniqueName (prefix = 'table-', listLocation = 'tables') {
      let max = 0
      for (let element of this.itemData[listLocation] ?? []) {
        const split = element.name?.split('-') ?? '0'
        const number = parseInt(split[split.length - 1])
        if (number > max) max = number
      }
      return prefix + ++max
    },
    configDetailAdded (item) {
      this.resetItemFieldErrorLookup()
      let x = Math.round(this.CANVAS_WIDTH / 6) - 50
      let y = Math.round(this.CANVAS_HEIGHT / 6) - 50
      while (this.xAxisMap[x] || this.yAxisMap[y]) x += 20, y += 20
      if (!(x >= this.CANVAS_WIDTH - 200 || y >= this.CANVAS_HEIGHT - 200)) item.x = x, item.y = y
      else return false
    },
    notValidNumber (val) {
      return isNaN(val) || val === null
    },
    itemResourceCommands () {
      return [
        {command: 'diningLocation', load: this.locations, loading: this.isLoading, params: {children: true}}
      ]
    },
    updateGroupNamesDelay () {
      // Wait for a short time to make sure elements are updated
      setTimeout(this.updateGroupNames, 200)
    },
    updateGroupNames () {
      const foundNames = {}
      this.groupNames = this.itemData.tables.reduce((a, t) => {
        const group = t.group
        if (group && !foundNames[group]) {
          foundNames[group] = true
          a.push(group)
        }
        return a
      }, [])
    },
    initFilteredTables () {
      if (!this.filteredTablesInit) {
        // First time we're here.  Set up some stuff
        this.filteredTablesInit = true
        this.filteredTableErrors = this.getPathErrors(this.jsonPath, 'tables')
        this.filteredTables = this.itemData.tables
      }
    },
    updateFilteredTables () {
      // Filter tables so any new tables are added to the list and any old tables are removed
      this.initFilteredTables()
      // Build lookup maps/lists
      const selectedLocationTables = this.selectedLocationTables
      const selectedLocationTableMap = selectedLocationTables.reduce((a, t) => {
        a[t.id] = t
        return a
      }, {})
      // Get any cached tables we had
      const fromCache = this.cachedTables.filter(t => selectedLocationTableMap[t.tableId])
      // Remove tables we'll be using from the cache, and store tables we'll no longer be using in the cache
      const moveToCache = this.itemData.tables.filter(t => !selectedLocationTableMap[t.tableId])
      this.cachedTables = this.cachedTables.filter(t => !selectedLocationTableMap[t.tableId]).concat(moveToCache)
      // Start with relevant tables from cache
      this.itemData.tables = this.itemData.tables.filter(t => selectedLocationTableMap[t.tableId]).concat(fromCache)
      // Get map of existing tables in configuration
      const configuredTableMap = (this.itemData.tables || []).reduce((a, t) => {
        a[t.tableId] = t
        return a
      }, {})

      const hasTables = !!selectedLocationTables.find(t => !!t.id)

      // Add any missing tables for this location
      selectedLocationTables.filter(t => !configuredTableMap[t.id]).forEach(t => {
        // Add the table item
        this.itemData.tables.push({
          id: null,
          tableConfigId: this.itemData.id || null,
          tableId: t.id,
          tableName: t.name,
          minCovers: null,
          maxCovers: null,
          maxJoinCovers: null,
          quantity: null,
          autoAssignQuantity: 0,
          definitionMinCovers: t.minCovers,
          definitionMaxCovers: t.maxCovers,
          definitionMaxJoinCovers: t.maxJoinCovers,
          definitionQuantity: t.quantity,
          definitionDisabled: t.disabled,
          pos: 0,
          disabled: hasTables,
          group: null
        })
      })

      // Update group names
      this.updateGroupNames()
    }
  }
}
</script>
